import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, ImagePlus } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.45, delay: Math.min(i, 7) * 0.04, ease: [0.16, 1, 0.3, 1] } }),
};

// Reusable image grid + lightbox. Shows a tasteful placeholder when empty.
export default function PhotoGrid({ images, loading, columns = 4, placeholderCount = 8, placeholderNote, minSlots = 0 }) {
  const [active, setActive] = useState(null);

  const close = useCallback(() => setActive(null), []);
  const prev = useCallback(() => setActive((i) => (i === null ? i : (i - 1 + images.length) % images.length)), [images.length]);
  const next = useCallback(() => setActive((i) => (i === null ? i : (i + 1) % images.length)), [images.length]);

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, close, prev, next]);

  const colClass = {
    2: "grid-cols-1 sm:grid-cols-2",
    3: "grid-cols-2 md:grid-cols-3",
    4: "grid-cols-2 md:grid-cols-3 lg:grid-cols-4",
  }[columns] || "grid-cols-2 md:grid-cols-3 lg:grid-cols-4";

  if (loading) {
    return (
      <div className={`grid ${colClass} gap-3 sm:gap-4`} data-testid="photo-grid-loading">
        {Array.from({ length: placeholderCount }).map((_, i) => (
          <div key={i} className="aspect-square bg-navy/[0.06] animate-pulse" />
        ))}
      </div>
    );
  }

  if (!images || images.length === 0) {
    return (
      <div data-testid="photo-placeholder">
        <div className={`grid ${colClass} gap-3 sm:gap-4`}>
          {Array.from({ length: placeholderCount }).map((_, i) => (
            <div key={i} className="aspect-square border border-dashed border-gold/30 bg-navy/[0.03] flex flex-col items-center justify-center text-center px-2">
              <ImagePlus className="h-6 w-6 text-gold/40" strokeWidth={1.3} />
              <span className="text-navy/30 text-[0.65rem] tracking-luxe uppercase mt-2">Photo Slot</span>
            </div>
          ))}
        </div>
        <p className="text-center text-navy/40 text-sm mt-6">
          {placeholderNote || "Project photos will appear here once uploaded from the admin dashboard."}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className={`grid ${colClass} gap-3 sm:gap-4`} data-testid="photo-grid">
        {images.map((img, i) => (
          <motion.button
            key={img.id}
            type="button"
            initial="hidden" animate="show" variants={fadeUp} custom={i}
            onClick={() => setActive(i)}
            data-testid={`photo-thumb-${i}`}
            className="group relative aspect-square overflow-hidden bg-navy focus:outline-none focus:ring-2 focus:ring-gold"
          >
            <img src={img.url} alt={img.caption || "Finished cabinetry install"} loading={i < columns ? "eager" : "lazy"} decoding="async" fetchPriority={i < columns ? "high" : "auto"} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
            <div className="absolute inset-0 group-hover:bg-navy-deep/30 transition-colors duration-500" />
            <div className="absolute inset-0 ring-1 ring-inset ring-gold/0 group-hover:ring-gold/50 transition-all duration-500" />
          </motion.button>
        ))}
        {minSlots > images.length &&
          Array.from({ length: minSlots - images.length }).map((_, i) => (
            <div key={`slot-${i}`} data-testid={`photo-empty-slot-${i}`}
              className="aspect-square border border-dashed border-gold/30 bg-navy/[0.03] flex flex-col items-center justify-center text-center px-2">
              <ImagePlus className="h-6 w-6 text-gold/40" strokeWidth={1.3} />
              <span className="text-navy/30 text-[0.65rem] tracking-luxe uppercase mt-2">Photo Coming Soon</span>
            </div>
          ))}
      </div>

      <AnimatePresence>
        {active !== null && images[active] && (
          <motion.div
            className="fixed inset-0 z-[100] bg-navy-deep/95 backdrop-blur-md flex items-center justify-center"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={close} data-testid="lightbox"
          >
            <button onClick={close} data-testid="lightbox-close" className="absolute top-6 right-6 text-ivory/70 hover:text-gold transition-colors z-10">
              <X className="h-8 w-8" strokeWidth={1.2} />
            </button>
            <button onClick={(e) => { e.stopPropagation(); prev(); }} data-testid="lightbox-prev" className="absolute left-4 sm:left-8 text-ivory/70 hover:text-gold transition-colors z-10 p-2">
              <ChevronLeft className="h-10 w-10" strokeWidth={1} />
            </button>
            <button onClick={(e) => { e.stopPropagation(); next(); }} data-testid="lightbox-next" className="absolute right-4 sm:right-8 text-ivory/70 hover:text-gold transition-colors z-10 p-2">
              <ChevronRight className="h-10 w-10" strokeWidth={1} />
            </button>
            <motion.img
              key={images[active].id}
              src={images[active].url}
              alt={images[active].caption || "Finished cabinetry install"}
              decoding="async"
              onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="max-h-[85vh] max-w-[90vw] object-contain border border-gold/30 shadow-2xl"
            />
            <p className="absolute bottom-6 left-0 right-0 text-center text-ivory/60 text-sm tracking-wider">
              {active + 1} / {images.length}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
